<template>
  <BaseTemplate app-name="Gestão de Equipamentos" page-title="">
    <template #content>
      <div class="w-full py-6 px-4 md:px-8">
        <!-- Botão Voltar -->
        <button @click="voltar"
          class="hidden md:inline-flex mb-5 items-center gap-2 bg-blue-500 dark:bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors dark:hover:bg-blue-700">
          <SquareArrowLeft class="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <!-- Cabeçalho com títulos e botões -->
        <div class="flex flex-col sm:flex-row justify-between items-center mb-6">
          <h1 class="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <HardDriveIcon class="w-6 h-6 text-blue-500" />
            Gestão de Equipamentos
          </h1>
          <div class="flex flex-wrap items-center gap-3 mt-4 sm:mt-0">
            <!-- Botão de Filtro -->
            <button @click="abrirModalFiltro"
              class="relative flex items-center gap-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-lg shadow">
              <FilterIcon class="w-5 h-5" />
              <span class="font-medium">Filtros</span>
              <span v-if="totalFiltros > 0"
                class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {{ totalFiltros }}
              </span>
            </button>
            <!-- Botão "Novo Equipamento" -->
            <button @click="openModal()"
              class="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow">
              <PlusIcon class="w-5 h-5" />
              Novo Equipamento
            </button>
          </div>
        </div>

        <!-- Filtro rápido (busca por nome/modelo) -->
        <div class="flex flex-col sm:flex-row gap-4 mb-6">
          <input v-model="filter" type="text" placeholder="Buscar por nome ou modelo..."
            class="w-full sm:w-1/2 border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500" />
        </div>

        <!-- Mensagem de erro -->
        <p v-if="error" class="text-red-500 text-center mb-4">{{ error }}</p>

        <!-- Modal de Filtro -->
        <div v-if="isFiltroModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          @click.self="fecharModalFiltro">
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96 animate-fade-in">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <FilterIcon class="w-5 h-5 text-blue-500" />
              Filtros
            </h2>

            <!-- Filtro por unidade -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unidade</label>
              <select v-model.number="filtroUnidade"
                class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500">
                <option value="">Todas</option>
                <option v-for="unidade in unidades" :key="unidade.id" :value="unidade.id">
                  {{ unidade.nome }}
                </option>
              </select>
            </div>

            <!-- Filtro por status -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select v-model="filtroStatus"
                class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500">
                <option value="">Todos os Status</option>
                <option value="em_funcionamento">Em Funcionamento</option>
                <option value="em_atendimento">Em Atendimento</option>
                <option value="parado">Parado</option>
              </select>
            </div>

            <!-- Filtro por canais -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Canais</label>
              <select v-model="filtroCanais"
                class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500">
                <option value="">Todos os Canais</option>
                <option value="16">16 Canais</option>
                <option value="64">64 Canais</option>
                <option value="80">80 Canais</option>
                <option value="128">128 Canais</option>
              </select>
            </div>

            <!-- Botões: Limpar / Aplicar -->
            <div class="flex justify-end space-x-3">
              <button @click="limparFiltros"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                Limpar Filtros
              </button>
              <button @click="aplicarFiltros"
                class="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg">
                Aplicar
              </button>
            </div>
          </div>
        </div>

        <!-- Tabela desktop (incluindo colunas Gantry, Peso, Largura Mesa) -->
        <div
          class="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto dark:text-white text-gray-800">
          <table class="w-full">
            <thead
              class="bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-300 text-sm uppercase tracking-wide rounded-t-lg">
              <tr>
                <th @click="ordenarPor('unidade')" class="cursor-pointer px-4 py-3 text-left">
                  Unidade <span v-if="colunaOrdenada === 'unidade'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('nome')" class="cursor-pointer px-4 py-3 text-left">
                  Nome <span v-if="colunaOrdenada === 'nome'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('modelo')" class="cursor-pointer px-4 py-3 text-left">
                  Modelo <span v-if="colunaOrdenada === 'modelo'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('canais')" class="cursor-pointer px-4 py-3 text-left">
                  Canais <span v-if="colunaOrdenada === 'canais'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('gantry')" class="cursor-pointer px-4 py-3 text-left">
                  Gantry(cm) <span v-if="colunaOrdenada === 'gantry'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('peso')" class="cursor-pointer px-4 py-3 text-left">
                  Peso(Kg) <span v-if="colunaOrdenada === 'peso'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('largura_mesa')" class="cursor-pointer px-4 py-3 text-left">
                  Largura Mesa(cm) <span v-if="colunaOrdenada === 'largura_mesa'">{{ ordemAscendente ? '🠕' : '🠗'
                  }}</span>
                </th>
                <th @click="ordenarPor('status')" class="cursor-pointer px-4 py-3 text-left">
                  Status <span v-if="colunaOrdenada === 'status'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th class="px-4 py-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="equipment in equipamentosOrdenados" :key="equipment.id"
                class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                <td class="px-4 py-3">{{ equipment.unidade }}</td>
                <td class="px-4 py-3">{{ equipment.nome }}</td>
                <td class="px-4 py-3">{{ equipment.modelo }}</td>
                <td class="px-4 py-3">{{ equipment.canais && equipment.canais != 0 ? equipment.canais : '' }}</td>
                <td class="px-4 py-3">{{ equipment.gantry && equipment.gantry != 0 ? equipment.gantry : '' }}</td>
                <td class="px-4 py-3">{{ equipment.peso ? equipment.peso : '' }}</td>
                <td class="px-4 py-3">{{ equipment.largura_mesa && equipment.largura_mesa != 0 ? equipment.largura_mesa : '' }}</td>
                <td class="px-4 py-3">{{ formatarStatus(equipment.status) }}</td>
                <td class="px-4 py-3 text-center">
                  <div class="flex justify-center space-x-2">
                    <button @click="openModal(equipment)"
                      class="flex items-center gap-1 text-blue-500 hover:text-blue-700">
                      <Edit2Icon class="w-4 h-4" /> Editar
                    </button>
                    <button @click="deleteEquipment(equipment.id)"
                      class="flex items-center gap-1 text-red-500 hover:text-red-700">
                      <Trash2Icon class="w-4 h-4" /> Excluir
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Versão mobile em cards (também exibe Gantry, Peso e Largura Mesa) -->
        <div class="sm:hidden space-y-6">
          <div v-for="equipment in equipamentosOrdenados" :key="equipment.id"
            class="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-6 border border-gray-100 dark:border-gray-700 transition transform hover:scale-105">
            <div class="flex justify-between items-center mb-2">
              <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ equipment.nome }}</h3>
              <div class="flex items-center gap-2">
                <button @click="openModal(equipment)" class="text-blue-500 hover:text-blue-700">
                  <Edit2Icon class="w-5 h-5" />
                </button>
                <button @click="deleteEquipment(equipment.id)" class="text-red-500 hover:text-red-700">
                  <Trash2Icon class="w-5 h-5" />
                </button>
              </div>
            </div>
            <p class="text-sm text-gray-700 dark:text-gray-300" v-if="equipment.modelo"><strong>Modelo:</strong> {{ equipment.modelo }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300" v-if="equipment.canais && equipment.canais != 0"><strong>Canais:</strong> {{ equipment.canais }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300" v-if="equipment.gantry && equipment.gantry != 0"><strong>Gantry(cm):</strong> {{ equipment.gantry }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300" v-if="equipment.peso"><strong>Peso(Kg):</strong> {{ equipment.peso }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300" v-if="equipment.largura_mesa && equipment.largura_mesa != 0"><strong>Largura Mesa(cm):</strong> {{ equipment.largura_mesa }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300"><strong>Status:</strong> {{
              formatarStatus(equipment.status) }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300"><strong>Unidade:</strong> {{ equipment.unidade }}</p>
          </div>
        </div>

        <!-- Modal de criação / edição incluindo Gantry, Peso e Largura Mesa -->
        <div v-if="isModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          @click.self="closeModal">
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96 animate-fade-in">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <HardDriveIcon class="w-5 h-5 text-blue-500" />
              {{ isEditMode ? "Editar Equipamento" : "Novo Equipamento" }}
            </h2>

            <!-- Campo Nome -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
            <input v-model="currentEquipment.nome" type="text"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3" />

            <!-- Campo Modelo -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Modelo</label>
            <input v-model="currentEquipment.modelo" type="text"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3" />

            <!-- Campo Canais -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Canais
            </label>
            <input v-model="currentEquipment.canais" type="text" inputmode="numeric" maxlength="3" required
              placeholder="Ex: 128"
              @input="currentEquipment.canais = currentEquipment.canais.replace(/\D/g, '').slice(0, 3)"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3" />

            <!-- Campo Gantry -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Gantry (cm)
            </label>
            <input v-model="currentEquipment.gantry" type="text" inputmode="numeric" maxlength="3" required
              placeholder="Ex: 78"
              @input="currentEquipment.gantry = currentEquipment.gantry.replace(/\D/g, '').slice(0, 3)"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3" />

            <!-- Campo Peso -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Peso (Kg)
            </label>
            <input v-model="currentEquipment.peso" type="text" inputmode="numeric" maxlength="3" required
              placeholder="Ex: 200"
              @input="currentEquipment.peso = currentEquipment.peso.replace(/\D/g, '').slice(0, 3)"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3" />

            <!-- Campo Largura da Mesa -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Largura Mesa (cm)
            </label>
            <input v-model="currentEquipment.largura_mesa" type="text" inputmode="numeric" maxlength="3" required
              placeholder="Ex: 45"
              @input="currentEquipment.largura_mesa = currentEquipment.largura_mesa.replace(/\D/g, '').slice(0, 3)"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3" />

            <!-- Campo Status -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select v-model="currentEquipment.status"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3">
              <option value="em_funcionamento">EM FUNCIONAMENTO</option>
              <option value="em_atendimento">EM ATENDIMENTO</option>
              <option value="parado">PARADO</option>
            </select>

            <!-- Campo Unidade -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unidade</label>
            <select v-model.number="currentEquipment.unidade_id"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3">
              <option value="" disabled>Selecione...</option>
              <option v-for="unidade in unidades" :key="unidade.id" :value="unidade.id">
                {{ unidade.nome }}
              </option>
            </select>

            <!-- Botões Salvar / Cancelar -->
            <div class="flex justify-end space-x-3">
              <button @click="closeModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button @click="saveEquipment"
                class="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg">
                <SaveIcon class="w-4 h-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </template>
  </BaseTemplate>
</template>

<script setup>
import apiClient from '@/services/authService';
import BaseTemplate from '../templates/BaseTemplate.vue';
import { ref, computed, onMounted } from 'vue';
import {
  HardDriveIcon,
  PlusIcon,
  Edit2Icon,
  Trash2Icon,
  XIcon,
  SaveIcon,
  FilterIcon,
  SquareArrowLeft
} from 'lucide-vue-next';
import { useRouter } from 'vue-router';

const router = useRouter();
const voltar = () => router.go(-1);

const equipments = ref([]);
const filter = ref('');
const error = ref(null);
const isModalOpen = ref(false);
const isEditMode = ref(false);

// Agora inclui gantry, peso e largura_mesa
const currentEquipment = ref({
  id: '',
  nome: '',
  modelo: '',
  status: 'em_funcionamento',
  unidade_id: '',       // será sempre number (v-model.number)
  canais: 16,
  gantry: 60,           // number
  peso: '',             // string
  largura_mesa: 43      // number
});

// Ordenação
const colunaOrdenada = ref('nome');
const ordemAscendente = ref(true);

// Modal de filtro
const isFiltroModalOpen = ref(false);

// Filtros
const filtroUnidade = ref('');
const filtroStatus = ref('');
const filtroCanais = ref('');

// Computed para contar filtros ativos
const totalFiltros = computed(() => {
  let count = 0;
  if (filtroUnidade.value) count++;
  if (filtroStatus.value) count++;
  if (filtroCanais.value) count++;
  return count;
});

// Lista de unidades
const unidades = ref([]);

// Filtragem reativa de equipamentos
const filteredEquipments = computed(() =>
  equipments.value.filter(equipment => {
    const nomeModeloMatch =
      equipment.nome.toLowerCase().includes(filter.value.toLowerCase()) ||
      equipment.modelo.toLowerCase().includes(filter.value.toLowerCase());

    const unidadeMatch =
      !filtroUnidade.value || Number(equipment.unidade_id) === Number(filtroUnidade.value);

    const statusMatch = !filtroStatus.value || equipment.status === filtroStatus.value;
    const canaisMatch =
      !filtroCanais.value || equipment.canais.toString() === filtroCanais.value;

    return nomeModeloMatch && unidadeMatch && statusMatch && canaisMatch;
  })
);

// Abre modal de filtro
function abrirModalFiltro() {
  isFiltroModalOpen.value = true;
}

// Fecha modal de filtro
function fecharModalFiltro() {
  isFiltroModalOpen.value = false;
}

// Limpa filtros
function limparFiltros() {
  filtroUnidade.value = '';
  filtroStatus.value = '';
  filtroCanais.value = '';
  fecharModalFiltro();
}

// Aplica filtros (basta fechar)
function aplicarFiltros() {
  fecharModalFiltro();
}

// Computed para ordenar
const equipamentosOrdenados = computed(() => {
  const arr = filteredEquipments.value.slice();
  arr.sort((a, b) => {
    let valorA, valorB;
    switch (colunaOrdenada.value) {
      case 'unidade':
        valorA = a.unidade.toLowerCase();
        valorB = b.unidade.toLowerCase();
        break;
      case 'nome':
        valorA = a.nome.toLowerCase();
        valorB = b.nome.toLowerCase();
        break;
      case 'modelo':
        valorA = a.modelo.toLowerCase();
        valorB = b.modelo.toLowerCase();
        break;
      case 'canais':
        valorA = a.canais;
        valorB = b.canais;
        break;
      case 'gantry':
        valorA = Number(a.gantry);
        valorB = Number(b.gantry);
        break;
      case 'peso':
        // Tentar converter para float, senão comparar strings
        valorA = parseFloat(a.peso) || a.peso || '';
        valorB = parseFloat(b.peso) || b.peso || '';
        break;
      case 'largura_mesa':
        valorA = Number(a.largura_mesa);
        valorB = Number(b.largura_mesa);
        break;
      case 'status':
        valorA = a.status.toLowerCase();
        valorB = b.status.toLowerCase();
        break;
      default:
        return 0;
    }
    if (valorA < valorB) return ordemAscendente.value ? -1 : 1;
    if (valorA > valorB) return ordemAscendente.value ? 1 : -1;
    return 0;
  });
  return arr;
});

// Formata status para exibição
function formatarStatus(status) {
  const statusMap = {
    em_funcionamento: 'Em funcionamento',
    em_atendimento: 'Em atendimento',
    parado: 'Parado'
  };
  return statusMap[status] || status;
}

// Alterna ordem ao clicar no header
function ordenarPor(coluna) {
  if (colunaOrdenada.value === coluna) {
    ordemAscendente.value = !ordemAscendente.value;
  } else {
    colunaOrdenada.value = coluna;
    ordemAscendente.value = true;
  }
}

onMounted(() => {
  fetchEquipments();
  fetchUnidades();
});

// Busca equipamentos (GET) e converte unidade_id para number
async function fetchEquipments() {
  try {
    const response = await apiClient.get(`/api/v1/equipments/`);
    equipments.value = response.data.map(item => ({
      id: item.id,
      nome: item.nome,
      modelo: item.modelo,
      status: item.status,
      canais: item.canais,
      gantry: item.gantry_value,                   // Usar valor numérico
      peso: item.peso,
      largura_mesa: item.largura_mesa_value,       // Usar valor numérico
      unidade: item.unidade,                       // Ex.: "Hospital X"
      unidade_id: item.unidade_id                  // Ex.: 5
    }));
  } catch (err) {
    error.value = 'Erro ao carregar equipamentos';
  }
}

// Busca unidades (GET)
async function fetchUnidades() {
  try {
    const response = await apiClient.get(`/api/v1/enterprises/`);
    unidades.value = response.data;
  } catch (err) {
    error.value = 'Erro ao carregar unidades';
  }
}

// Abre modal de criação/edição
function openModal(equipment = null) {
  isEditMode.value = !!equipment;
  if (equipment) {
    console.log('Equipment selecionado:', equipment); // Depuração
    currentEquipment.value.id = equipment.id;
    currentEquipment.value.nome = equipment.nome;
    currentEquipment.value.modelo = equipment.modelo;
    currentEquipment.value.status = equipment.status;
    currentEquipment.value.canais = Number(equipment.canais) || '';
    currentEquipment.value.gantry = Number(equipment.gantry) || '';
    currentEquipment.value.peso = equipment.peso;
    currentEquipment.value.largura_mesa = Number(equipment.largura_mesa) || '';
    currentEquipment.value.unidade_id = Number(equipment.unidade_id) || '';
  } else {
    currentEquipment.value.id = '';
    currentEquipment.value.nome = '';
    currentEquipment.value.modelo = '';
    currentEquipment.value.status = 'em_funcionamento';
    currentEquipment.value.unidade_id = '';
    currentEquipment.value.canais = '';
    currentEquipment.value.gantry = '';
    currentEquipment.value.peso = '';
    currentEquipment.value.largura_mesa = '';
  }
  isModalOpen.value = true;
}

// Fecha modal
function closeModal() {
  isModalOpen.value = false;
}

// Salva equipamento (POST ou PUT), convertendo unidade_id para number
async function saveEquipment() {
  // validação de campos obrigatórios
  if (!currentEquipment.value.nome || !currentEquipment.value.status || !currentEquipment.value.unidade_id) {
    error.value = 'Preencha Nome, Status e Unidade antes de salvar.';
    return;
  }
  try {
    const payload = {
      nome: currentEquipment.value.nome,
      modelo: currentEquipment.value.modelo,
      status: currentEquipment.value.status,
      canais: Number(currentEquipment.value.canais),
      gantry: Number(currentEquipment.value.gantry),
      peso: currentEquipment.value.peso,
      largura_mesa: Number(currentEquipment.value.largura_mesa),
      unidade_id: Number(currentEquipment.value.unidade_id)
    };

    if (isEditMode.value) {
      const response = await apiClient.put(
        `/api/v1/equipments/update/${currentEquipment.value.id}/`,
        payload
      );
      const updated = response.data;
      const idx = equipments.value.findIndex(e => e.id === updated.id);
      if (idx !== -1) {
        equipments.value[idx] = {
          id: updated.id,
          nome: updated.nome,
          modelo: updated.modelo,
          status: updated.status,
          canais: updated.canais,
          gantry: updated.gantry_value,                   // Usar valor numérico
          peso: updated.peso,
          largura_mesa: updated.largura_mesa_value,       // Usar valor numérico
          unidade: updated.unidade,
          unidade_id: updated.unidade_id
        };
      }
    } else {
      const response = await apiClient.post(`/api/v1/equipments/create/`, payload);
      const created = response.data;
      equipments.value.push({
        id: created.id,
        nome: created.nome,
        modelo: created.modelo,
        status: created.status,
        canais: created.canais,
        gantry: created.gantry_value,                   // Usar valor numérico
        peso: created.peso,
        largura_mesa: created.largura_mesa_value,       // Usar valor numérico
        unidade: created.unidade,
        unidade_id: created.unidade_id
      });
    }

    closeModal();
  } catch (err) {
    error.value = err.response?.data?.detail || 'Erro ao salvar equipamento';
  }
}

// Exclui equipamento (DELETE)
async function deleteEquipment(equipmentId) {
  const confirmDeletion = window.confirm(
    'Tem certeza de que deseja excluir este equipamento? Esta ação não pode ser desfeita e isso afetará o Dashboard.'
  );
  if (!confirmDeletion) return;

  try {
    await apiClient.delete(`/api/v1/equipments/delete/${equipmentId}/`);
    equipments.value = equipments.value.filter(e => e.id !== equipmentId);
  } catch (err) {
    error.value = err.response?.data?.detail || 'Erro ao deletar equipamento';
  }
}
</script>
